import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { cancelReservation, returnReservationEarly } from '../utils/reservationOperations';
import { getAllItems } from '../utils/itemOperations';
import Layout from '../components/layout/Layout';

const MyReservations = () => {
  const { currentUser, userDetails } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUserReservations();
  }, [currentUser, userDetails]);

  const loadUserReservations = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      // First, get all items to build a lookup map
      const allItems = await getAllItems();
      const itemsMap = {};
      allItems.forEach(item => {
        itemsMap[item.id] = item;
      });
      setItems(itemsMap);

      // Get all reservations across all items for this user
      const allReservations = [];
      
      // Query each item's reservations subcollection
      for (const item of allItems) {
        try {
          const reservationsRef = collection(db, 'items', item.id, 'reservations');
          let q;
          
          if (userDetails?.pid) {
            // PID users: query by PID
            q = query(
              reservationsRef,
              where('pid', '==', userDetails.pid)
            );
          } else {
            // Email/password users: query by userId
            q = query(
              reservationsRef,
              where('userId', '==', currentUser.uid)
            );
          }

          const snapshot = await getDocs(q);
          
          snapshot.forEach(doc => {
            const data = doc.data();
            const now = new Date();
            const endTime = data.end instanceof Object && data.end.toDate ? data.end.toDate() : new Date(data.end);
            
            // Check if reservation is expired
            let status = data.status;
            if ((status === 'reserved' || status === 'checked_out') && endTime < now) {
              status = 'expired';
            }
            
            // Filter for active and expired reservations (but not canceled or returned)
            if (status === 'reserved' || status === 'checked_out' || status === 'expired') {
              allReservations.push({
                id: doc.id,
                itemId: item.id,
                itemName: item.name,
                ...data,
                status: status, // Use the computed status
                isExpired: status === 'expired'
              });
            }
          });
        } catch (itemError) {
          console.error(`Error querying reservations for item ${item.id}:`, itemError);
          // Continue with other items
        }
      }

      // Sort by start date
      allReservations.sort((a, b) => a.start.toMillis() - b.start.toMillis());
      setReservations(allReservations);

    } catch (error) {
      console.error('Error loading reservations:', error);
      setError('Failed to load your reservations');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnReservation = async (itemId, reservationId) => {
    try {
      setError('');
      setSuccess('');
      
      await returnReservationEarly(itemId, reservationId);
      setSuccess('Reservation returned successfully!');
      
      // Reload reservations
      loadUserReservations();
    } catch (error) {
      console.error('Error returning reservation:', error);
      setError('Failed to return reservation: ' + (error.message || 'Unknown error'));
    }
  };

  const formatDateTime = (timestamp) => {
    return new Date(timestamp.toDate()).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case 'reserved':
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case 'checked_out':
        return `${baseClasses} bg-orange-100 text-orange-800`;
      case 'expired':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'reserved':
        return 'Reserved';
      case 'checked_out':
        return 'Checked Out';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">Loading your reservations...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Reservations</h1>
          <p className="mt-2 text-gray-600">
            Manage your current and upcoming equipment reservations
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md">
            {success}
          </div>
        )}

        {reservations.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">No reservations found</h3>
            <p className="text-gray-600">
              You don't have any active reservations. Visit the equipment catalog to make a reservation.
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Item
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Start Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      End Time
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reservations.map((reservation) => (
                    <tr key={`${reservation.itemId}-${reservation.id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {reservation.itemName}
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {reservation.itemId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(reservation.start)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(reservation.end)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(reservation.status)}>
                          {getStatusText(reservation.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {reservation.notes || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <a
                          href={`/item/${reservation.itemId}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View Item
                        </a>
                        {(reservation.status === 'reserved' || reservation.status === 'checked_out') && !reservation.isExpired && (
                          <button
                            onClick={() => handleReturnReservation(reservation.itemId, reservation.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Return Early
                          </button>
                        )}
                        {reservation.isExpired && (
                          <span className="text-gray-500 text-sm">Expired</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyReservations;
